import Authentication from '@components/AuthenticationForm';
import Loader from '@components/Common/Loader';
import logo from '@assets/mintylLogo.png';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { Typography } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import './index.scss';

interface LoginProps {
    isLoading: boolean;
    loginUserProfile: any;
}

const getLoginErrorMessage = (error: any) => {
    if (typeof error === 'string' && error.trim()) return error;
    if (error?.message) return error.message;
    return 'Check the email and password and try again.';
};

/**
 * Platform scale, shown as three facts rather than a feature list.
 *
 * Counted, not written down. These were constants ("84,300 job seekers") at three to four times
 * the truth, on the one screen every member of staff sees every day. The screen is pre-auth, but
 * the counts are public and come from the same backend query the site's marketing figures use,
 * through `GET /api/auth/sign-in-figures`, which caches them for an hour.
 *
 * A figure that cannot be counted is left out rather than guessed, and if none can be, the row
 * does not render at all.
 */
interface SignInFigures {
    activeJobs: number | null;
    hiringBrands: number | null;
    candidates: number | null;
}

const FIGURE_LABELS: { key: keyof SignInFigures; label: string }[] = [
    { key: 'activeJobs', label: 'Open roles' },
    { key: 'hiringBrands', label: 'Hiring brands' },
    { key: 'candidates', label: 'Job seekers' },
];

export const signInFigures = (figures: SignInFigures | null) =>
    FIGURE_LABELS.flatMap(({ key, label }) => {
        const value = figures?.[key];
        return typeof value === 'number' && value > 0
            ? [{ label, value: value.toLocaleString('en-IN') }]
            : [];
    });

const Login: React.FC<LoginProps> = ({ loginUserProfile, isLoading }) => {
    const [figures, setFigures] = useState<SignInFigures | null>(null);

    useEffect(() => {
        let live = true;
        axios
            .get<SignInFigures>('/api/auth/sign-in-figures')
            .then((r) => { if (live) setFigures(r.data); })
            .catch(() => { /* No figures is a valid screen; a guess is not. */ });
        return () => { live = false; };
    }, []);

    const shownFigures = signInFigures(figures);
    const showForm =
        loginUserProfile?.status === 'idle' ||
        loginUserProfile?.status === 'failed' ||
        loginUserProfile?.status === 'logout';
    // Once shown, the form stays mounted through the sign-in request (under the loader) instead of
    // unmounting while it is 'loading'. Remounting would tear down the Turnstile widget and start
    // its challenge from zero, so a retry after a refusal would wait for a token all over again.
    const [formShown, setFormShown] = useState(false);
    if (showForm && !formShown) setFormShown(true);

    const hasError = loginUserProfile?.status === 'failed' && loginUserProfile?.error;

    return (
        <div className="login-page">
            {isLoading && <Loader />}

            {/* ── Left: brand panel. Hidden below 768px. ── */}
            <aside className="login-brand-panel">
                <div className="login-brand-content">
                    <div className="login-lockup">
                        <img alt="Mintly" className="login-lockup-mark" src={logo} />
                        <span className="login-lockup-overline">Control Center</span>
                    </div>

                    <h1 className="login-brand-headline">
                        The console behind every Mintly placement.
                    </h1>

                    <p className="login-brand-positioning">
                        Jobs, candidates, employers and Premium Hiring clients, tracked in one
                        place, scoped to your role.
                    </p>

                    {shownFigures.length > 0 && (
                    <dl className="login-figures">
                        {shownFigures.map((figure) => (
                            <div className="login-figure" key={figure.label}>
                                <dt className="login-figure-value">{figure.value}</dt>
                                <dd className="login-figure-label">{figure.label}</dd>
                            </div>
                        ))}
                    </dl>
                    )}
                </div>
            </aside>

            {/* ── Right: form panel ── */}
            <main className="login-form-panel">
                <div className="login-form-wrap">
                    {/* Below 768px the brand panel is gone, so the mark moves here. */}
                    <div className="login-lockup login-lockup--mobile">
                        <img alt="Mintly" className="login-lockup-mark" src={logo} />
                        <span className="login-lockup-overline">Control Center</span>
                    </div>

                    <Typography component="h2" className="login-title">
                        Sign in
                    </Typography>
                    <Typography className="login-subtitle">
                        Control Center access is restricted to Mintly staff.
                    </Typography>

                    {(showForm || formShown) && (
                        <div className="login-form-card">
                            {hasError && (
                                <div className="login-error" role="alert">
                                    <ErrorOutlineIcon className="login-error-icon" />
                                    <div>
                                        <div className="login-error-title">Sign-in failed</div>
                                        <div className="login-error-body">
                                            {getLoginErrorMessage(loginUserProfile.error)}
                                        </div>
                                    </div>
                                </div>
                            )}
                            <Authentication />
                        </div>
                    )}

                    <div className="login-footnote">
                        <LockOutlinedIcon className="login-footnote-icon" />
                        <span>Sessions are audited. Access is granted per role in User RBAC.</span>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Login;
